/** The tenant's postpay overrides, as carried by the public tenant config. */
export type PostpayLegalTexts = {
  paymentTermsDe?: string | null;
  paymentTermsEn?: string | null;
  noWithdrawalDe?: string | null;
  noWithdrawalEn?: string | null;
  agbNoteDe?: string | null;
  agbNoteEn?: string | null;
};

export type PostpayTextField = "paymentTerms" | "noWithdrawal" | "agbNote";

/**
 * The payment target to state when the runtime tenant config carries none
 * (#2352). Not a second opinion on the tenant's setting: operational tenants
 * always receive a resolved number from `GET /app/tenant-config/:slug`, which
 * applies this same default server-side. This covers only the case where no
 * runtime config loaded at all — a pinned (bundled-config) build.
 *
 * MIRROR of `DEFAULT_PAYMENT_TERM_DAYS` in `backend/src/core/lib/postpayTerms.ts`
 * (the mail) and `new-backend/app/src/lib/postpayTerms.ts` (the config
 * endpoint). Three packages that cannot import each other; change one, change
 * all three. Pinned by a test in each.
 */
export const DEFAULT_PAYMENT_TERM_DAYS = 14;

/**
 * The wording to show for one postpay contract term: the tenant's own where it
 * saved one, else kolula's default (#1702/#1703).
 *
 * Pure and separately tested because this is the one place the acceptance
 * SCREEN could silently disagree with the acceptance EMAIL — the mail applies
 * the same rule server-side in `backend/src/core/lib/email/postpayBlocks.ts`,
 * and two different contract terms shown to the same customer is the failure
 * that matters here.
 *
 * Blank counts as absent, exactly as the backend treats it: a tenant that
 * cleared a field gets kolula's default back, never an empty contract term.
 */
export const resolveLegalText = (
  overrides: PostpayLegalTexts | null | undefined,
  language: string,
  field: PostpayTextField,
  fallback: string
): string => {
  const suffix = language?.startsWith("de") ? "De" : "En";
  const stored = overrides?.[`${field}${suffix}` as keyof PostpayLegalTexts];
  return typeof stored === "string" && stored.trim() ? stored.trim() : fallback;
};
